import React from "react";
import _ from "lodash";
import { css } from "@emotion/react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/styles";
import { formatDate } from "features/helpers/utils";
import { StyledIssueSignature, StyledIssue, StyledTitle } from "./Issue.styles";
import { useIntl } from "react-intl";

function parseJiraDescription(document) {
  let result = {
    ticketUrl: null,
    reporter: null,
    cleanedDescription: "",
    images: [],
  };

  if (!document) return result;

  const rootContent = document.content;

  for (const e of rootContent) {
    switch (e.type) {
      case "paragraph": {
        const innerContent = _.get(e, "content", []);
        let currTxt = "";
        for (const obj of innerContent) {
          currTxt += _.get(obj, "text", "");

          if (currTxt.startsWith("Attachments:") && _.get(obj, "marks")) {
            for (const e of _.get(obj, "marks")) {
              if (_.get(e, "type") === "link") {
                let link = _.get(e, "attrs.href", null)
                  ? result.images.push(_.get(e, "attrs.href"))
                  : "";
              }
            }
          }
        }

        if (currTxt.length) {
          if (currTxt.startsWith("Ticket url:")) {
            result.ticketUrl = currTxt.substring("Ticket url:".length).trim();
          } else if (currTxt.startsWith("Reporter:")) {
            result.reporter = currTxt.substring("Reporter:".length).trim();
          } else if (currTxt.startsWith("Attachments:")) {
            // ignore not to display
          } else {
            result.cleanedDescription += `${currTxt}\n`;
          }
        }
        break;
      }

      case "mediaSingle": {
        const innerContent = _.get(e, "content", []);
        for (const obj of innerContent) {
          if (_.get(obj, "type", "") === "media") {
            const attrs = _.get(obj, "attrs");
            if (attrs.type === "external") {
              result.images.push(attrs.url);
            }
          }
        }
        break;
      }

      case "mediaGroup": {
        const innerContent = _.get(e, "content", []);
        for (const obj of innerContent) {
          const attrs = _.get(obj, "attrs");
          if (attrs.type === "external") {
            result.images.push(attrs.url);
          }
        }
        break;
      }

      default: {
        // ignore rest
      }
    }
  }

  return result;
}

function parseLinearDescription(rawDescription) {
  const result = {
    ticketUrl: null,
    reporter: null,
    cleanedDescription: null,
    images: [],
  };

  if (!rawDescription) {
    return result;
  }

  // Extract ticket URL
  const ticketUrlMatch = rawDescription.match(/Ticket URL: \[(.*?)\]/);
  if (ticketUrlMatch && ticketUrlMatch[1]) {
    result.ticketUrl = ticketUrlMatch[1];
  }

  // Extract reporter
  const reporterMatch = rawDescription.match(
    /Reporter: (.*?)(?=\s+Description:|$)/
  );
  if (reporterMatch && reporterMatch[1]) {
    result.reporter = reporterMatch[1].trim();
  }

  // Extract images/attachments
  const attachmentSection = rawDescription.split("Attachments:")[1] || "";
  const allContent = attachmentSection + rawDescription;

  // Find all image markdown
  const imageMatches = [
    ...allContent.matchAll(/!\[.*?\]\((https?:\/\/[^\s\)]+)\)/g),
  ];

  // Find attachment patterns
  const attachmentMatches = [
    ...attachmentSection.matchAll(/\[attachment\]\((https?:\/\/[^\s\)]+)\)/g),
  ];

  // Combine unique URLs and filter for images only
  const urlSet = new Set([
    ...imageMatches.map((match) => match[1]),
    ...attachmentMatches.map((match) => match[1]),
  ]);

  result.images = Array.from(urlSet)
    .filter((url) => url !== result.ticketUrl)
    .filter((url) => {
      const lowerUrl = url.toLowerCase();
      return (
        lowerUrl.endsWith(".jpg") ||
        lowerUrl.endsWith(".jpeg") ||
        lowerUrl.endsWith(".png") ||
        lowerUrl.endsWith(".gif") ||
        lowerUrl.endsWith(".svg") ||
        rawDescription.includes(`![`)
      );
    });

  // Extract clean description
  // First try to get content between "Description:" and "Attachments:"
  const descriptionRegex = /Description:\s*(.*?)(?=\s*(?:Attachments:|$))/s;
  const descriptionMatch = rawDescription.match(descriptionRegex);

  if (descriptionMatch && descriptionMatch[1]) {
    // Clean up the extracted description
    result.cleanedDescription = descriptionMatch[1]
      .trim()
      // Remove any markdown links/images
      .replace(/\[.*?\]\(.*?\)/g, "")
      .replace(/!\[.*?\]\(.*?\)/g, "")
      .trim();
  } else {
    // If no "Description:" label found, just clean the entire text
    result.cleanedDescription = rawDescription
      .replace(/Ticket URL:.*?(?=\s|$)/g, "")
      .replace(/Reporter:.*?(?=\s|$)/g, "")
      .replace(/Description:/g, "")
      .replace(/Attachments:.*$/s, "")
      .replace(/\[.*?\]\(.*?\)/g, "")
      .replace(/!\[.*?\]\(.*?\)/g, "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line)
      .join("\n")
      .trim();
  }

  return result;
}

/** The priority of the issue. 0 = No priority, 1 = Urgent, 2 = High, 3 = Normal, 4 = Low. */
const priorityMapping = {
  0: "No priority",
  1: "Urgent",
  2: "High",
  3: "Normal",
  4: "Low",
};

function IssueDetailCard({
  type,
  issue,
  position,
  isShowDetachOption = true,
  detachIssue = () => {},
}) {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const {
    identifier = "",
    title,
    teamName: team,
    projectName = "",
    description,
    assignee,
    status,
    priority,
    issueId,
    issueUrl,
    createdAt,
  } = issue || {};

  const { ticketUrl, reporter, images, cleanedDescription } =
    type === "linear"
      ? parseLinearDescription(description)
      : parseJiraDescription(description);

  let issueSignature = `Ticket url: ${ticketUrl ?? "-"}\n\nReporter: ${
    reporter ?? "-"
  }\n`;

  function handleViewIssue() {
    window.open(issueUrl, "_blank");
  }

  function renderField(title, value) {
    return (
      <Box
        display={"flex"}
        color={theme.customColors.grey[600]}
        fontSize={"12px"}
        padding={"12px 0px"}
        className="hover-border"
        sx={{
          borderBottom: "1px solid #F4F4F4",
        }}>
        <span style={{ flex: 2 }}>{title}</span>
        <span style={{ flex: 4 }}>{value ?? "-"}</span>
      </Box>
    );
  }

  return (
    <StyledIssue
      style={{
        display: "flex",
        flexDirection: "column",
        padding: 12,
        boxShadow: "none",
        border: `1px solid ${theme.customColors.grey[300]}`,
      }}
      css={css`
        &:hover {
          box-sizing: border-box;
          background: ${theme.customColors.purple[300]};
          border: 1px solid ${theme.customColors.purple[500]} !important;
        }
      `}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <StyledTitle
          style={{
            color: theme.customColors.purple[500],
            fontWeight: theme.fontWeight.BOLD,
            fontSize: 13,
          }}>
          {identifier}
        </StyledTitle>
        <div style={{ display: "flex" }}>
          {isShowDetachOption && (
            <>
              <span
                style={{
                  fontSize: 12,
                  color: theme.customColors.purple[500],
                  cursor: "pointer",
                }}
                onClick={() => detachIssue(issueId)}>
                {formatMessage({ id: "linearIssue.btn.detach" })}
              </span>
              <Box
                width="1px"
                bgcolor={theme.customColors.grey[300]}
                mx="8px"
                height="10px"
                my="2px"
              />
            </>
          )}
          <span
            style={{
              fontSize: 12,
              color: theme.customColors.purple[500],
              cursor: "pointer",
            }}
            onClick={handleViewIssue}>
            {type === "linear"
              ? formatMessage({ id: "linearIssue.btn.goToView" })
              : formatMessage({ id: "jiraIssue.btn.goToView" })}
          </span>
        </div>
      </div>
      <StyledTitle style={{ margin: "12px 0px 8px" }}>
        {title ?? "-"}
      </StyledTitle>
      {type === "linear"
        ? renderField(
            formatMessage({ id: "linearIssue.selection.labelName.team" }),
            team
          )
        : renderField("Project", projectName)}
      {renderField(
        formatMessage({ id: "linearIssue.selection.labelName.assignee" }),
        assignee
      )}
      {renderField(
        formatMessage({ id: "linearIssue.selection.labelName.status" }),
        status
      )}
      <Box
        display={"flex"}
        color={theme.customColors.grey[600]}
        fontSize={"12px"}
        padding={"12px 0px"}
        className="hover-border"
        sx={{ borderBottom: "1px solid #F4F4F4" }}>
        <span style={{ flex: 2 }}>
          {formatMessage({ id: "global.header.prioirty" })}
        </span>
        <span style={{ flex: 4 }}>
          {type === "linear" ? priorityMapping[priority] : priority}
        </span>
      </Box>
      <Accordion
        key={position}
        disableGutters
        id={position}
        className="hover-border"
        sx={{
          "&:before": {
            display: "none",
          },
          background: "none",
          borderRadius: "0px",
          marginBottom: "8px",
          boxShadow: "none",
          borderBottom: "1px solid #F4F4F4",

          "&:hover": {
            // background: "#FFFFFF",
          },

          "&.Mui-expanded": {
            margin: "0 0 8px 0",
            borderRadius: "0px",
          },

          "& .MuiAccordionSummary-root": {
            minHeight: 0,
            padding: "8px 0px",
          },

          "& .MuiAccordionSummary-content": {
            margin: "0",

            "&.Mui-expanded": {
              margin: "0",
            },
          },

          "& .MuiAccordion-region": {
            borderRadius: "0 0 8px 8px",
          },

          "& .MuiAccordionDetails-root": {
            padding: 0,
            borderTop: "none",
          },
        }}>
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              sx={{
                color: "#7B61FF",
                fontSize: "20px",
                "&.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            />
          }
          sx={{
            "& .MuiAccordionSummary-expandIconWrapper": {
              marginRight: "4px",
            },
          }}>
          <span
            style={{
              color: theme.customColors.grey[600],
              fontSize: 12,
            }}>
            {formatMessage({ id: "global.column.description" })}
          </span>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              color: theme.customColors.grey[500],
              fontSize: "12px",
              lineHeight: "16px",
              wordBreak: "break-word",
            }}>
            {cleanedDescription || description}
            <StyledIssueSignature>
              <div
                style={{
                  padding: "30px 0px 12px",
                  display: "flex",
                  alignItems: "center",
                }}>
                <div
                  style={{
                    flex: 1,
                    borderBottom: `1px dashed  ${theme.customColors.grey[500]}`,
                  }}
                />
                <span style={{ margin: "0px 4px" }}>{"signature"}</span>
                <div
                  style={{
                    flex: 1,
                    borderBottom: `1px dashed  ${theme.customColors.grey[500]}`,
                  }}
                />
              </div>
              <p>{issueSignature}</p>
            </StyledIssueSignature>
            <div
              style={{
                display: "flex",
                gap: 4,
                margin: "8px 0px",
                flexWrap: "wrap",
              }}>
              {images &&
                images.length > 0 &&
                _.map(images, (url) => {
                  const extension = url.split(".").pop().toLowerCase();

                  return extension !== "pdf" ? (
                    <img
                      src={url}
                      style={{
                        maxHeight: 83,
                        maxWidth: 83,
                        borderRadius: 8,
                        objectFit: "contain",
                      }}
                      loading="eager"
                    />
                  ) : (
                    <span
                      className="startRow"
                      style={{
                        padding: 4,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        window.open(url, "_blank");
                      }}>
                      <img
                        src={require("assets/img/pdf.svg").default}
                        alt=""
                        style={{ width: 25 }}
                      />
                      <div style={{ padding: 9 }}>
                        <div style={{ fontWeight: 700 }}>
                          {_.truncate(url.split("/").pop(), {
                            length: 10,
                          })}
                        </div>
                        PDF
                      </div>
                    </span>
                  );
                })}
            </div>
          </Box>
        </AccordionDetails>
      </Accordion>
      <Box
        display={"flex"}
        color={theme.customColors.grey[500]}
        fontSize={"12px"}
        padding={"8px 0px"}>
        <span style={{ flex: 2 }}>
          {formatDate(createdAt ?? new Date(), { time: true })}
        </span>
      </Box>
    </StyledIssue>
  );
}

export default IssueDetailCard;
